import React, { useState, useEffect } from 'react';
import { 
  Search, Globe, FileText, AlertTriangle, CheckCircle2, 
  ExternalLink, RefreshCw, ChevronDown, ChevronUp, Edit3,
  Eye, EyeOff, Link, Image, Tag, TrendingUp, AlertCircle,
  BarChart2, Target, Loader2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SEO Manager Component for CMS
 * Provides SEO audit, editing capabilities, and optimization suggestions
 */
const SEOManager = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('audit');
  const [auditData, setAuditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [seoForm, setSeoForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  // Keyword suggestions for MEP/Building Services
  const keywordSuggestions = {
    services: [
      'MEP engineering UK', 'building services consultancy', 'mechanical design London',
      'electrical engineering consultants', 'public health design', 'HVAC design UK',
      'sustainability consultancy', 'net zero building design', 'Part L compliance',
      'BREEAM assessment', 'building physics', 'decarbonisation consultants'
    ],
    sectors: [
      'residential MEP consultants', 'commercial building services', 'healthcare MEP design',
      'education building services', 'retail MEP engineering', 'hospitality building services',
      'industrial MEP design', 'mixed-use development engineering'
    ],
    locations: [
      'MEP consultants London', 'building services engineers South East',
      'mechanical engineers UK', 'electrical consultants Greater London',
      'MEP engineering consultancy nationwide'
    ]
  };

  // Run SEO audit
  const runAudit = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/seo/audit`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAuditData(data);
      }
    } catch (error) {
      console.error('Audit failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load SEO settings for an item
  const loadSEOSettings = async (type, id) => {
    try {
      const response = await fetch(`${API_URL}/api/seo/settings/${type}/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSeoForm(data);
        setSelectedItem({ type, id, name: data.metaTitle || id });
      }
    } catch (error) {
      console.error('Failed to load SEO settings:', error);
    }
  };

  // Save SEO settings
  const saveSEOSettings = async () => {
    if (!selectedItem) return;
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/seo/settings/${selectedItem.type}/${selectedItem.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(seoForm)
        }
      );
      if (response.ok) {
        // Refresh audit after save
        runAudit();
      }
    } catch (error) {
      console.error('Failed to save SEO settings:', error);
    } finally {
      setSaving(false);
    }
  };

  // Initial audit
  useEffect(() => {
    runAudit();
  }, []);

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Get severity badge color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Calculate title/description length indicators
  const getTitleStatus = (title) => {
    if (!title) return { status: 'missing', color: 'text-red-500', message: 'Missing' };
    const len = title.length;
    if (len < 30) return { status: 'short', color: 'text-amber-500', message: `Too short (${len}/50-60)` };
    if (len > 60) return { status: 'long', color: 'text-amber-500', message: `Too long (${len}/50-60)` };
    return { status: 'good', color: 'text-green-500', message: `Good (${len}/50-60)` };
  };

  const getDescriptionStatus = (desc) => {
    if (!desc) return { status: 'missing', color: 'text-red-500', message: 'Missing' };
    const len = desc.length;
    if (len < 120) return { status: 'short', color: 'text-amber-500', message: `Too short (${len}/150-160)` };
    if (len > 160) return { status: 'long', color: 'text-amber-500', message: `Too long (${len}/150-160)` };
    return { status: 'good', color: 'text-green-500', message: `Good (${len}/150-160)` };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            SEO Manager
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Optimise your website for search engines
          </p>
        </div>
        <button
          onClick={runAudit}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Run Audit
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {[
            { id: 'audit', label: 'SEO Audit', icon: BarChart2 },
            { id: 'editor', label: 'Page Editor', icon: Edit3 },
            { id: 'keywords', label: 'Keywords', icon: Tag },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Audit Tab */}
      {activeTab === 'audit' && auditData && (
        <div className="space-y-6">
          {/* Score Card */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">SEO Score</span>
                <Target className="w-4 h-4 text-gray-400" />
              </div>
              <div className={`text-3xl font-bold mt-2 ${
                auditData.summary.score >= 80 ? 'text-green-600' :
                auditData.summary.score >= 50 ? 'text-amber-600' : 'text-red-600'
              }`}>
                {auditData.summary.score}/100
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">High Priority</span>
                <AlertTriangle className="w-4 h-4 text-red-500" />
              </div>
              <div className="text-3xl font-bold mt-2 text-red-600">
                {auditData.summary.high_severity}
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Medium Priority</span>
                <AlertCircle className="w-4 h-4 text-amber-500" />
              </div>
              <div className="text-3xl font-bold mt-2 text-amber-600">
                {auditData.summary.medium_severity}
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Issues</span>
                <FileText className="w-4 h-4 text-gray-400" />
              </div>
              <div className="text-3xl font-bold mt-2 text-gray-900">
                {auditData.summary.total_issues}
              </div>
            </div>
          </div>

          {/* Issues List */}
          {auditData.issues.length > 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="font-medium text-gray-900">Issues Found</h3>
              </div>
              <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                {auditData.issues.map((issue, index) => (
                  <div 
                    key={index}
                    className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      loadSEOSettings(issue.type, issue.id);
                      setActiveTab('editor');
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded border ${getSeverityColor(issue.severity)}`}>
                        {issue.severity.toUpperCase()}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{issue.name}</p>
                        <p className="text-xs text-gray-500">{issue.type} · {issue.issue}</p>
                      </div>
                    </div>
                    <Edit3 className="w-4 h-4 text-gray-400" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="font-medium text-green-800">All Clear!</h3>
              <p className="text-sm text-green-600 mt-1">No SEO issues found. Great job!</p>
            </div>
          )}
        </div>
      )}

      {/* Editor Tab */}
      {activeTab === 'editor' && (
        <div className="space-y-6">
          {selectedItem ? (
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">
                    Editing: {selectedItem.name}
                  </h3>
                  <p className="text-xs text-gray-500">{selectedItem.type} · {selectedItem.id}</p>
                </div>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                {/* Meta Title */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-700">Meta Title</label>
                    <span className={`text-xs ${getTitleStatus(seoForm.metaTitle).color}`}>
                      {getTitleStatus(seoForm.metaTitle).message}
                    </span>
                  </div>
                  <input
                    type="text"
                    value={seoForm.metaTitle || ''}
                    onChange={(e) => setSeoForm({...seoForm, metaTitle: e.target.value})}
                    placeholder="Enter SEO-optimised title (50-60 characters)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Meta Description */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-700">Meta Description</label>
                    <span className={`text-xs ${getDescriptionStatus(seoForm.metaDescription).color}`}>
                      {getDescriptionStatus(seoForm.metaDescription).message}
                    </span>
                  </div>
                  <textarea
                    value={seoForm.metaDescription || ''}
                    onChange={(e) => setSeoForm({...seoForm, metaDescription: e.target.value})}
                    placeholder="Write a compelling description (150-160 characters)"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* SEO Keywords */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    SEO Keywords
                  </label>
                  <input
                    type="text"
                    value={seoForm.seoKeywords || ''}
                    onChange={(e) => setSeoForm({...seoForm, seoKeywords: e.target.value})}
                    placeholder="keyword 1, keyword 2, keyword 3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Separate keywords with commas</p>
                </div>

                {/* Canonical URL */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Canonical URL (Optional)
                  </label>
                  <input
                    type="text"
                    value={seoForm.canonicalUrl || ''}
                    onChange={(e) => setSeoForm({...seoForm, canonicalUrl: e.target.value})}
                    placeholder="https://www.furd.co.uk/..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* OG Image */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Social Share Image (OG Image)
                  </label>
                  <input
                    type="text"
                    value={seoForm.ogImage || ''}
                    onChange={(e) => setSeoForm({...seoForm, ogImage: e.target.value})}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Indexing Options */}
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={seoForm.noIndex || false}
                      onChange={(e) => setSeoForm({...seoForm, noIndex: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">No Index</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={seoForm.noFollow || false}
                      onChange={(e) => setSeoForm({...seoForm, noFollow: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">No Follow</span>
                  </label>
                </div>

                {/* SERP Preview */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Search Preview</h4>
                  <div className="bg-white border border-gray-200 rounded p-3">
                    <p className="text-blue-700 text-lg hover:underline cursor-pointer">
                      {seoForm.metaTitle || 'Page Title | FURD Ltd'}
                    </p>
                    <p className="text-green-700 text-sm">
                      www.furd.co.uk/{selectedItem.type}/{seoForm.slug || selectedItem.id}
                    </p>
                    <p className="text-gray-600 text-sm mt-1">
                      {seoForm.metaDescription || 'Add a meta description to see preview...'}
                    </p>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <button
                    onClick={saveSEOSettings}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Save SEO Settings
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <Search className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h3 className="font-medium text-gray-900">Select a Page to Edit</h3>
              <p className="text-sm text-gray-600 mt-1">
                Click on an issue from the Audit tab or select a page from the list below
              </p>
              
              {/* Quick select buttons - Projects excluded, SEO focus on Services */}
              <div className="mt-6 flex flex-wrap gap-2 justify-center">
                {['services', 'sectors', 'team', 'news'].map(type => (
                  <button
                    key={type}
                    onClick={() => {
                      // Load first item of this type
                      if (auditData?.issues) {
                        const item = auditData.issues.find(i => i.type === type);
                        if (item) loadSEOSettings(item.type, item.id);
                      }
                    }}
                    className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Keywords Tab */}
      {activeTab === 'keywords' && (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 flex items-center gap-2">
              <Target className="w-4 h-4" />
              Keyword Strategy for MEP & Building Services
            </h4>
            <p className="text-sm text-blue-700 mt-1">
              Use these high-intent keywords to target UK clients in the building services and construction sectors.
            </p>
          </div>

          {/* Keyword Categories */}
          {Object.entries(keywordSuggestions).map(([category, keywords]) => (
            <div key={category} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection(category)}
                className="w-full px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between"
              >
                <h3 className="font-medium text-gray-900 capitalize">{category} Keywords</h3>
                {expandedSections[category] ? (
                  <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </button>
              {expandedSections[category] && (
                <div className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {keywords.map((keyword, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-blue-100 hover:text-blue-700 cursor-pointer transition-colors"
                        onClick={() => {
                          // Copy to clipboard
                          navigator.clipboard.writeText(keyword);
                        }}
                        title="Click to copy"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Keyword Tips */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">SEO Best Practices</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Use 1 primary keyword per page in the title, H1, and first paragraph
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Include location-based keywords (London, UK, South East) for local SEO
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Write meta descriptions that include a call-to-action
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Link services to relevant projects and sector pages
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Publish regular insights targeting client questions
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default SEOManager;
